import React, { useState, useEffect } from 'react';
import { Truck, Users, Package, Home, Building2, AlertCircle, CheckCircle } from 'lucide-react';
import QuoteButton from '../components/Quote/QuoteButton';
import QuoteModal from '../components/Quote/QuoteModal';



const pricingServices = [
    {
        title: "Loading & Unloading",
        price: "$160/hr",
        description: "2 professional movers",
        Icon: Users,
        details: "Perfect for when you have your own transportation. Our team handles all the heavy lifting.",
    },
    {
        title: "Small Move",
        price: "$185/hr",
        description: "16' truck + 2 movers",
        Icon: Package,
        details: "Ideal for studio or 1-bedroom apartments with moderate furniture.",
    },
    {
        title: "Large Move",
        price: "$240/hr",
        description: "26' truck + 3 movers",
        Icon: Building2,
        details: "Perfect for 4+ bedroom homes or office relocations.",
    },
    {
        title: "Full Service",
        price: "$290/hr",
        description: "26' truck + 4 movers",
        Icon: Truck,
        details: "Our premium service with maximum efficiency and care.",
    },
];

const additionalCharges = [
    {
        title: "TV Boxes",
        price: "$55 per TV",
        description: "Professional packing of TVs with our own TV boxes to ensure safe transport.",
    },
    {
        title: "Floor Protection",
        price: "$200 flat fee",
        description: "Protect your floors with our high-quality floor protection materials during the move.",
    },
    {
        title: "Piano & Gun Safe Moving",
        price: "$100 flat fee",
        description: (
            <>
                Specialized handling and equipment for moving pianos and gun safes safely and securely.
                <br />
                <span className="font-semibold">**Applies to upright and spinet pianos**</span>
            </>
        ),
    },
    {
        title: "Overnight Trucking",
        price: "$250 flat fee",
        description: "Need your belongings moved overnight? We offer overnight trucking services for an additional flat fee, ensuring your move is completed on your schedule.",
    },
    {
        title: "Credit/ Debit Card Processing",
        price: "+3% of total",
        description: "We accept credit and debit card payments for your convenience. A 3% processing fee will be added to the total cost of your move when using this payment method.",
    },
    {
        title: "Disconnecting/ Reconnecting Washer & Dryer",
        price: "$50 flat fee",
        description: "Our team can disconnect and reconnect your washer and dryer for a smooth transition to your new home.",
    },
];

const guidelines = [
    {
        title: "Minimum Service Charge",
        description: (
            <>
                All moves have a minimum 2-hour service charge to ensure proper setup and professionalism. The clock begins when our team arrives on-site. <span className="font-semibold">After the first 2 hours, billing is in 15-minute increments based on the hourly rate of your chosen service.</span>
            </>
        ),
    },
    {
        title: "No Hidden Fees",
        description: "We pride ourselves on transparent pricing. The rates listed are all-inclusive for the services described, with no hidden fees or surprise charges.",
    },
    {
        title: "Fuel Surcharge",
        description: "A fuel surcharge may apply for moves over 50 miles from our service area.",
    },
    {
        title: "Weekend Overtime Rates",
        description: "Moves scheduled on weekends may be subject to overtime rates. Please inquire when booking for specific details.",
    },
    
];

const PricingCard = ({ title, price, description, Icon, details }) => (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100 hover:border-red-200">
        <div className="bg-red-50 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
            <Icon className="h-8 w-8 text-red-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-red-600 font-bold text-3xl mb-4">{price}</p>
        <p className="text-gray-600 text-sm mb-4 font-medium">{description}</p>
        <p className="text-gray-700 leading-relaxed">{details}</p>
    </div>
);

const AdditionalChargeCard = ({ title, price, description }) => (
    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="flex items-start gap-4">
            <AlertCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
            <div className="flex-1">
                <h4 className="text-lg font-semibold text-gray-900 mb-1">{title}</h4>
                <p className="text-orange-600 font-semibold mb-2">{price}</p>
                <p className="text-gray-600 text-sm">{description}</p>
            </div>
        </div>
    </div>
);

const GuidelineCard = ({ title, description }) => (
    <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-start gap-4">
            <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{title}</h4>
                <p className="text-gray-700 text-sm leading-relaxed">{description}</p>
            </div>
        </div>
    </div>
);

const Pricing = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

    return (
        <>
            <section className="pt-16 pb-12 md:pb-20 px-4 bg-gradient-to-br from-red-50 to-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Pricing</h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Transparent, competitive rates for all your moving needs. No hidden fees, just honest pricing.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 max-w-5xl mx-auto">
                        {pricingServices.map((service, index) => (
                            <PricingCard key={index} {...service} />
                        ))}
                    </div>

                    <div className="text-center">
                        <QuoteButton onClick={() => setIsQuoteModalOpen(true)} />
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-24 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">Additional Charges</h2>
                        <div className="h-1 w-24 bg-red-600 mx-auto"></div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {additionalCharges.map((charge, index) => (
                            <AdditionalChargeCard key={index} {...charge} />
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-24 px-4 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">Pricing Guidelines</h2>
                        <div className="h-1 w-24 bg-blue-600 mx-auto"></div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {guidelines.map((guideline, index) => (
                            <GuidelineCard key={index} {...guideline} />
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-20 px-4 bg-red-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Move?</h2>
                    <p className="text-red-100 text-lg mb-8 max-w-2xl mx-auto">
                        Get a free, no-obligation quote. Our team will reach out within 24 hours to finalize your moving date.
                    </p>
                    <button
                        onClick={() => setIsQuoteModalOpen(true)}
                        className="inline-block bg-white text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-red-50 transition-colors duration-300"
                    >
                        Get Your Free Quote
                    </button>
                </div>
            </section>

            <QuoteModal
                isOpen={isQuoteModalOpen}
                onClose={() => setIsQuoteModalOpen(false)}
            />
        </>
    );
};

export default Pricing;
